import { useCallback, useEffect, useRef } from 'react';
import { useCombatStore } from '../store/combatStore';
import { useAuthStore } from '../auth/authStore';
import type {
  ServerMessage,
  ClientMessage,
  WSActionType,
  CombatAction,
} from '../types/interfaces';

const WS_URL = import.meta.env.VITE_WS_URL ?? 'ws://localhost:8080';
const RECONNECT_DELAY_MS = 3000;

interface UseWebSocketOptions {
  combatId: string;
  entityId: string;
  toast?: {
    success: (t: string) => void;
    error: (t: string) => void;
    info: (t: string) => void;
  };
}

export interface UseWebSocketReturn {
  sendAction:  (type: WSActionType, action?: CombatAction) => void;
  /** Permanently closes the socket without scheduling a reconnect. */
  disconnect:  () => void;
  connected:   boolean;
}

export function useWebSocket({ combatId, entityId, toast }: UseWebSocketOptions): UseWebSocketReturn {
  const wsRef          = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // When false the onclose handler will NOT schedule a reconnect.
  // Set to false by disconnect() so that a deliberate close (combat ended,
  // player navigated away) doesn't waste resources reconnecting.
  const shouldReconnect = useRef(true);

  const { setCombat, setWsConnected, wsConnected } = useCombatStore();

  const connect = useCallback(() => {
    // Already open or actively connecting — do nothing
    const state = wsRef.current?.readyState;
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;

    // Toda tentativa de conexão volta a permitir auto-reconnect. Sem isto, se
    // uma batalha anterior terminou (disconnect() ⇒ shouldReconnect=false) e o
    // combatId muda sem remontar o componente (ex.: "Nova Batalha"), o socket
    // novo ficava sem reconectar num soluço e a tela travava até dar F5.
    shouldReconnect.current = true;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      // Wipe any stale combat from a previous battle so the result overlay
      // from the last session does not flash before the new state arrives.
      // IMPORTANT: resetCombat() sets ui.myEntityId = null. The setMyEntityId
      // effect in useCombat only re-fires when the *prop* changes, not when
      // the store is reset — so we must restore it immediately here from the
      // closure value before any messages are sent.
      useCombatStore.getState().resetCombat();
      useCombatStore.getState().setMyEntityId(entityId);
      setWsConnected(true);
      // Include the bearer token so the server can validate entity ownership
      const token = useAuthStore.getState().token ?? '';
      const msg: ClientMessage = {
        type: 'JOIN_COMBAT',
        combat_id: combatId,
        entity_id: entityId,
        token,
      } as ClientMessage & { token: string };
      ws.send(JSON.stringify(msg));
    };

    ws.onmessage = (event: MessageEvent<string>) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        console.error('[WS] Invalid JSON from server:', event.data);
        return;
      }

      // console.log('[WS] ←', message);

      switch (message.type) {
        case 'COMBAT_STATE':
          try {
            // Um COMBAT_STATE válido significa que a batalha carregou — limpa
            // qualquer erro anterior (ex.: um ERROR transitório da 1ª tentativa)
            // que senão ficaria preso na tela de "loading" para sempre.
            useCombatStore.getState().setServerError(null);
            // `movements` (opcional) traz a sequência COMPLETA de células que
            // cada entidade percorreu — o CombatGrid usa isso pra animar o
            // personagem andando casa a casa, sem deslizar direto pro destino.
            setCombat(message.state, message.movements);
          } catch (err) {
            console.error('[WS] setCombat threw:', err, 'state=', message.state);
          }
          break;
        case 'CONNECTED':
          console.info(`[WS] Joined combat ${message.combat_id}`);
          break;
        case 'ERROR': {
          const errMsg = (message as any).error || (message as any).message || 'Erro desconhecido do servidor';
          console.error(`[WS] Server error: ${errMsg}`);
          useCombatStore.getState().setServerError(errMsg);
          if (toast) {
            toast.error(errMsg);
          }
          break;
        }
        case 'INFO': {
          if (toast) {
            toast.info((message as any).message);
          }
          break;
        }
        default:
          console.warn('[WS] Unknown message type — server may be out of date:', message);
          break;
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      // Auto-reconnect only when the socket closed unexpectedly.
      // disconnect() sets shouldReconnect = false to suppress this.
      if (wsRef.current === ws && shouldReconnect.current) {
        reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };

    ws.onerror = (err) => {
      // DO NOT call ws.close() here — the browser closes the socket
      // automatically after an error.  Calling close() on a CONNECTING socket
      // produces "WebSocket is closed before the connection is established."
      // The onclose handler below will fire next and schedule the reconnect.
      console.error('[WS] Connection error:', err);
    };
  }, [combatId, entityId, setCombat, setWsConnected]);

  useEffect(() => {
    connect();
    return () => {
      // Cancel any pending reconnect timer
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      // Null out ALL handlers before closing.
      //
      // React <StrictMode> runs cleanup + remount in dev to test side-effect
      // hygiene.  When we call .close() on a still-CONNECTING socket Chrome
      // always logs "WebSocket is closed before the connection is established"
      // — that browser-level warning cannot be suppressed from JS and is
      // harmless (the second mount reconnects cleanly).
      //
      // What we CAN prevent is our own handlers firing during that teardown:
      //   • onclose = null  →  no ghost reconnect timer
      //   • onerror = null  →  no "[WS] Connection error" log on cleanup
      //   • onopen / onmessage = null  →  stale callbacks can't mutate state
      if (wsRef.current) {
        wsRef.current.onopen    = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror   = null;
        wsRef.current.onclose   = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  /**
   * Permanently closes the WebSocket without scheduling a reconnect.
   * Call this when the combat is over or the player deliberately exits,
   * so the server-side slot is freed immediately rather than at unmount.
   */
  const disconnect = useCallback(() => {
    shouldReconnect.current = false;

    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }

    if (wsRef.current) {
      wsRef.current.onopen    = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror   = null;
      wsRef.current.onclose   = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setWsConnected(false);
  }, [setWsConnected]);

  const sendAction = useCallback(
    (type: WSActionType, action?: CombatAction) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        console.warn('[WS] Attempted to send without an active connection.');
        return;
      }
      const msg: ClientMessage = {
        type,
        combat_id: combatId,
        entity_id: entityId,
        action,
      };
      wsRef.current.send(JSON.stringify(msg));
    },
    [combatId, entityId],
  );

  return { sendAction, disconnect, connected: wsConnected };
}
